import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { matchPath, useLocation } from 'react-router-dom';

import { LinkPreserveSearch } from '@/components/LinkPreserveSearch';
import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { decodeParams } from '@/utils/text';

type Params = Record<string, string>;

type Matches = {
  key: CanvasRoutes;
  cfg: (typeof RouterConfig)[CanvasRoutes];
  href: string;
  params: Params;
  isCurrent: boolean;
};

function getLabel(cfg: { name: string; breadcrumb?: string | ((p: Params) => string) }, params: Params) {
  if (typeof cfg.breadcrumb === 'function') return cfg.breadcrumb(params);
  if (typeof cfg.breadcrumb === 'string') return cfg.breadcrumb;
  return cfg.name;
}

// Extra trailing crumbs appended after the route-derived trail (e.g. the
// SKU × Site context on the Product / Stocking Locations page, or the
// Backtest Results/Backtest Scenario link-back on Value Opportunity). The
// last crumb overall renders as the current page (plain text); earlier ones
// are clickable when `href` is given, plain text otherwise.
export interface ExtraCrumb {
  label: string;
  href?: string;
  // When true, this crumb's link drops the current page's query string instead of carrying
  // it forward (LinkPreserveSearch's default within a section) — e.g. "Backtest Results" is a
  // plain list page that reads none of the persona params a deeper page like Product/Stocking
  // Locations has accumulated, so it shouldn't inherit them.
  clearSearch?: boolean;
}

export function Breadcrumbs({ extra = [] }: { extra?: ExtraCrumb[] }) {
  const { pathname } = useLocation();

  const entries = Object.entries(RouterConfig) as Array<[CanvasRoutes, (typeof RouterConfig)[CanvasRoutes]]>;

  const matches = entries
    .map(([key, cfg]) => {
      if (cfg.showInBreadcrumbs === false) return null;

      // Parent match (so /catalog/products /catalog/products/details/6C-LID-G)
      const m = matchPath({ path: cfg.path, end: false }, pathname);
      if (!m) return null;

      // Current match (exact)
      const isCurrent = !!matchPath({ path: cfg.path, end: true }, pathname);

      return {
        key,
        cfg,
        href: m.pathnameBase,
        params: decodeParams((m.params ?? {}) as Params),
        isCurrent,
      };
    })
    .filter(Boolean) as Matches[];

  if (!matches.length) return null;

  matches.sort((a, b) => a.href.length - b.href.length);

  // Optional grouping crumb (e.g. "Production Optimization") from the deepest
  // (current) route — captured before the current-page crumb is popped below, so
  // it renders right after the section root: "Decisions › Group › ...".
  const lastCfg = matches[matches.length - 1]?.cfg as
    | { breadcrumbGroup?: string; breadcrumbShowCurrent?: boolean }
    | undefined;
  const group = lastCfg?.breadcrumbGroup;

  // Drop the current page's own crumb so the trail shows only its ancestors
  // (e.g. "/foundation-forecasting/home" -> "Foundation Forecasting"), unless the
  // route opts in to keeping it (`breadcrumbShowCurrent`) — e.g. because a page
  // passes `extra` crumbs that need this crumb as their anchor (Product /
  // Stocking Locations), or because the page heading doesn't already show this
  // route's name (Backtest Results, Backtest Scenario).
  const last = matches[matches.length - 1];
  if (matches.length > 1 && last.isCurrent && !lastCfg?.breadcrumbShowCurrent) {
    matches.pop();
  }

  // Each route-derived crumb links to its own resolved path (e.g.
  // "/decisions/backtests" for Backtest Results, not the section root) — so a
  // kept current-page crumb like Backtest Scenario links to itself rather than
  // bouncing back up to an ancestor.
  const [rootMatch, ...restMatches] = matches;
  // Decisions-specific: its section root is a label, not a working link (its own
  // "page" is just a redirect to the section's landing route), and its current-page
  // crumb (see `forceNonLink` below) is always plain text even when kept in the
  // trail. Other sections keep their original self-linking-crumb behavior.
  const isDecisionsSection = rootMatch?.key === CanvasRoutes.Decisions;
  const crumbs: Array<{ key: string; label: string; href: string | null; clearSearch?: boolean }> = [
    ...(rootMatch
      ? [
          {
            key: rootMatch.href,
            label: getLabel(rootMatch.cfg, rootMatch.params),
            href: isDecisionsSection ? null : rootMatch.href,
          },
        ]
      : []),
    ...(group ? [{ key: `group-${group}`, label: group, href: null }] : []),
    ...restMatches.map((m) => ({ key: m.href, label: getLabel(m.cfg, m.params), href: m.href })),
    ...extra.map((e, i) => ({
      key: `extra-${i}-${e.label}`,
      label: e.label,
      href: e.href ?? null,
      clearSearch: e.clearSearch,
    })),
  ];

  return (
    <nav aria-label="Breadcrumb" className="flex">
      <ol role="list" className="flex items-center space-x-1 text-[12px]">
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          // Decisions always shows its current-page crumb as plain text, even when
          // it resolves to a real (self-)link — other sections keep the original
          // behavior of linking the last crumb when it has an href.
          const forceNonLink = isLast && isDecisionsSection;

          return (
            <li key={c.key} className="m-0">
              <div className="flex items-center">
                {idx ? <ChevronRightIcon aria-hidden="true" className="size-3 shrink-0 text-[#9295A5] mx-0.5" /> : null}

                {c.href && !forceNonLink ? (
                  <LinkPreserveSearch
                    // An object `to` with an explicit (even empty) `search` wins over
                    // LinkPreserveSearch's own carry-the-current-query-forward default — see
                    // ExtraCrumb.clearSearch.
                    to={c.clearSearch ? { pathname: c.href, search: '' } : c.href}
                    aria-current={isLast ? 'page' : undefined}
                    className="font-medium text-[#274483] hover:text-[#4A6BAD]"
                  >
                    {c.label}
                  </LinkPreserveSearch>
                ) : (
                  <span aria-current={isLast ? 'page' : undefined} className="font-medium text-[#9295A5]">
                    {c.label}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
